import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GoalsService } from '../goals.service';
import { BaseClass } from 'src/app/shared/classes/base';
import { SubordinateGoalPreview } from '../_interfaces/subordinate-goals-preview.interface';
import { SubordinateGoalStatus } from '../_interfaces/subordinate-goals-status.enum';
import { LoadingController, ToastController } from '@ionic/angular';
import { AuthService } from 'src/app/shared/services/auth.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { UtilService } from "../../shared/services/util.service";

@Component({
    selector: 'app-goals-review-validation',
    templateUrl: './goals-review-validation.page.html'
})
export class GoalsReviewValidationPage extends BaseClass implements OnDestroy {

    public goals: Array<SubordinateGoalPreview>;
    public displayedGoals: Array<SubordinateGoalPreview>;
    public SubordinateGoalStatus = SubordinateGoalStatus;
    public isAllowedToReopen: boolean = false;
    public processTitle: string;
    public language = this._translationService.getSelectedLanguage();

    private _userRoles: Array<string> = [];
    private readonly _cycleId: number;
    private _searchSubject: Subject<string> = new Subject();
    private readonly _processId: number;

    constructor(
            protected _toastController: ToastController,
            protected _translationService: TranslationService,
            private _loadingController: LoadingController,
            private _authService: AuthService,
            private _router: Router,
            private _goalsService: GoalsService,
            private _activatedRoute: ActivatedRoute,
            private _utilService: UtilService,
    ) {
        super(_toastController, _translationService);

        this._processId = this._router.getCurrentNavigation().extras.state
                ? parseInt(this._router.getCurrentNavigation().extras.state.processId)
                :null;
        this._utilService.insertProcessIdInLocalStorage(this._processId);

        this._cycleId = parseInt(
                this._activatedRoute.snapshot.paramMap.get('cycleId'), 10
        );
        this._userRoles = this._authService.getUserRoles();
        if (this._userRoles.includes('Administrator') ||
                this._userRoles.includes('HR')) {
            this.isAllowedToReopen = true;
        }
        this._setSearchSubscription();
    }

    ionViewWillEnter() {
        this._loadSubordinates(this._cycleId);
    }

    public goBack(): void {
        this._utilService.removeProcessIdFromLocalStorage();
        this._router.navigate(['dashboard']);
    }

    ionViewDidLeave() {
        this._utilService.removeProcessIdFromLocalStorage();
    }

    public goToValidation(user: SubordinateGoalPreview): void {
        this._router.navigate(['goals-review-validation/' + this._cycleId + '/' + user.id]);
    }

    public getStatusColor(status: SubordinateGoalStatus): string {
        switch (status) {
            case SubordinateGoalStatus.Validated:
                return 'success';
            case SubordinateGoalStatus.WaitingChanges:
                return 'warning';
            case SubordinateGoalStatus.Pending:
            case SubordinateGoalStatus.WaitingValidation:
            default:
                return 'primary';
        }
    }

    public updateSearch(searchTextValue: string) {
        this._searchSubject.next(searchTextValue);
    }

    private async _loadSubordinates(cycleId: number): Promise<void> {
        const loading = await this._loadingController.create();
        await loading.present();

        this._goalsService.getSubordinatesForReview(
                cycleId
        ).subscribe((response) => {
            this.goals = response.data.users;
            this.displayedGoals = response.data.users;
            this.processTitle = response.data.process.globalizationConfig ? response.data.process.globalizationConfig[this.language].title:'';
            loading.dismiss();

        }, (error) => {
            loading.dismiss();
            this.getErrorNotification(error);
        });
    }

    private _setSearchSubscription() {
        this._searchSubject.pipe(
                distinctUntilChanged(),
                debounceTime(1000)
        ).subscribe((searchValue: string) => {
            this.displayedGoals = this.goals.filter(goal => {
                return goal.name.toLowerCase().includes(
                        searchValue.toLowerCase().trim()
                );
            });
        });
    }

    public reopenValidation(goal: SubordinateGoalPreview): void {
        this._goalsService.reopenGoalsReviewValidationForUser(
                this._cycleId, goal.id
        ).subscribe((response) => {
                    this._loadSubordinates(this._cycleId);
                    this.notifySuccess('performanceCycles.processReopened');
                }
                , (error) => this.notifyError(error));
    }


    ngOnDestroy() {
        this._searchSubject.unsubscribe();
    }
}
